import { Component, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as monaco from 'monaco-editor';
import loader from '@monaco-editor/loader';
import { TablesService } from '../../core/service/table.service';
import { UUID } from '../../pages/aichat/aichat.model';
import { SchemaSettings } from '../../layout/shell/shell.model';

@Component({
  selector: 'app-json-view',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="editor-wrapper">
      <div #editorContainer class="editor-container"></div>
    </div>
  `,
  styles: [`
    .editor-wrapper {
      height: 90vh;
      width: 100%;
    }
    .editor-container {
      width: 100%;
      height: 100%;
      border: 1px solid #ccc;
    }
  `]
})
export class JsonViewComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('editorContainer') editorContainer!: ElementRef;
  @Output() jsonSchemaChanged = new EventEmitter<string>();
  @Output() formValid = new EventEmitter<boolean>();
  @Output() schemaSettingsChanged = new EventEmitter<SchemaSettings>();
  @Input() jsonSchema: string = '';
  @Input() tableId?: UUID;
  @Input() schemaSettings?: SchemaSettings;
  @Input() schemaConfig?: any;
  @Input() viewMode: 'schema' | 'data' = 'schema';
  @Input() readOnly = false;
  
  private lastReceivedSchema: string = '';
  private generatedSchema: any = null;
  private isSaving = false;
  private _isFormValid = false;
  private readonly autoSaveDebounceTime = 500; 
  private isUpdatingFromInput = false;
  
  private editor: monaco.editor.IStandaloneCodeEditor | null = null;
  private defaultValue = `{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "type": "object",
  "properties": {
    // Add your schema here
  }
}`;
  private resizeListener: (() => void) | null = null;

  set isFormValid(valid: boolean) {
    this._isFormValid = valid;
    this.formValid.emit(valid);
  }
  get isFormValid() {
    return this._isFormValid;
  }

  constructor(private tablesService: TablesService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tableId']) {
      console.log('JSON View: TableId changed to:', changes['tableId'].currentValue);
      this.generatedSchema = null;
      this.isFormValid = false;
    }
    
    if (changes['jsonSchema'] && this.editor) {
      let newValue = changes['jsonSchema'].currentValue;
      
      if (!newValue || newValue === 'null' || newValue === '') {
        this.isUpdatingFromInput = true;
        this.lastReceivedSchema = '';
        this.generatedSchema = null;
        
        console.log('JSON View: Setting default template for new/empty dataspace');
        
        this.editor.setValue(this.defaultValue);
        const formatAction = this.editor.getAction('editor.action.formatDocument');
        if (formatAction) {
          formatAction.run();
        }
        
        setTimeout(() => {
          this.isUpdatingFromInput = false;
        }, 100);
        return;
      }
      
      newValue = this.formatJsonForView(newValue);
      
      // Only update if the new value is different from what's currently in the editor
      const currentEditorValue = this.editor.getValue();
      if (newValue && newValue !== currentEditorValue) {
        this.isUpdatingFromInput = true;
        this.lastReceivedSchema = newValue;
        
        console.log('JSON View: Loading schema from dataspace');
        
        this.editor.setValue(newValue);
        const formatAction = this.editor.getAction('editor.action.formatDocument');
        if (formatAction) {
          formatAction.run();
        }
        
        setTimeout(() => {
          this.isUpdatingFromInput = false;
        }, 100);
      }
    }
  }

  ngAfterViewInit() {
    loader.init().then((monaco) => {
      const initialValue = (this.jsonSchema && this.jsonSchema !== 'null' && this.jsonSchema !== '') 
        ? this.formatJsonForView(this.jsonSchema)
        : (this.viewMode === 'schema' ? this.defaultValue : '[]');
      
      console.log('JSON View: Initializing editor with value:', initialValue ? 'schema data' : 'default template');
      
      this.lastReceivedSchema = initialValue;

      this.editor = monaco.editor.create(this.editorContainer.nativeElement, {
        readOnly: this.readOnly,
        value: initialValue,
        language: 'json',
        theme: 'vs',
        automaticLayout: true,
        minimap: {
          enabled: false
        },
        scrollBeyondLastLine: false,
        lineNumbers: 'on',
        roundedSelection: false,
        dimension: {
          width: this.editorContainer.nativeElement.offsetWidth,
          height: this.editorContainer.nativeElement.offsetHeight
        },
        scrollbar: {
          vertical: 'visible',
          horizontal: 'visible',
          useShadows: false,
          verticalHasArrows: false,
          horizontalHasArrows: false,
        }
      });

      this.resizeListener = () => {
        if (this.editor) {
          this.editor.layout({
            width: this.editorContainer.nativeElement.offsetWidth,
            height: this.editorContainer.nativeElement.offsetHeight
          });
        }
      };
      window.addEventListener('resize', this.resizeListener);

      let timeoutId: any = null;
      this.editor.onDidChangeModelContent(() => {
        if (this.isUpdatingFromInput) {
          return;
        }

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
          this.processSchemaChange();
        }, this.autoSaveDebounceTime); 
      });
    });
  }

  private processSchemaChange() {
    try {
      if (this.readOnly || this.viewMode === 'data') {
        return;
      }
      const value = this.editor?.getValue() || '';
      
      if (!value.trim() || value === this.lastReceivedSchema) {
        return;
      }

      const jsonObj = JSON.parse(value);
      this.isFormValid = true;
      this.lastReceivedSchema = value;

      const schema = jsonObj?.json_schema ?? jsonObj;
      this.generatedSchema = schema;

      if (jsonObj?.configuration) {
        const config = jsonObj.configuration;
        this.schemaSettings = {
          requireProvenance: config.require_provenance ?? this.schemaSettings?.requireProvenance ?? false,
          fastConversion: config.fast_conversion ?? this.schemaSettings?.fastConversion ?? false,
          multiple_rows: config.multiple_rows ?? this.schemaSettings?.multiple_rows ?? false,
          schemaSplittingThreshold: config.schema_splitting_threshold ?? this.schemaSettings?.schemaSplittingThreshold ?? 20,
          n_shot: config.n_shot ?? this.schemaSettings?.n_shot ?? 1
        };
        this.schemaSettingsChanged.emit(this.schemaSettings);
        if (config.sheets) {
          this.schemaConfig = {
            ...(this.schemaConfig ?? {}),
            configuration: {
              ...(this.schemaConfig?.configuration ?? {}),
              sheets: config.sheets
            }
          };
        }
      }

      this.jsonSchemaChanged.emit(JSON.stringify(schema));

      this.isSaving = true;
      this.sendSchemaToBackend(jsonObj);
      setTimeout(() => this.isSaving = false, 500);
      
    } catch (e) {
      console.warn('JSON View: Invalid JSON input:', e);
      this.isFormValid = false;
    }
  }

  sendSchemaToBackend(rawJson?: any) {
    if (!this.tableId) {
      return;
    }

    const payload = rawJson ?? this.generatedSchema;
    if (!payload) {
      return;
    }

    const body = payload.json_schema != null
      ? payload
      : { json_schema: payload };

    this.tablesService.postSchemaRaw(this.tableId, body).subscribe(
      () => {
        console.log('Schema saved to backend successfully');
      },
      (error: any) => {
        console.error('Failed to save schema to backend:', error);
      }
    );
  }

  ngOnDestroy() {
    if (this.editor) {
      this.editor.dispose();
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  private safeParseJson(value: string): any {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  }

  private formatJsonForView(value: any): string {
    let schemaObject = value;
    if (typeof value === 'string') {
      const parsed = this.safeParseJson(value);
      schemaObject = parsed;
    }
    if (typeof schemaObject === 'string') {
      schemaObject = this.safeParseJson(schemaObject);
    }

    if (this.viewMode === 'schema' && schemaObject && typeof schemaObject === 'object' && !schemaObject.json_schema) {
      const dto: any = { json_schema: schemaObject };
      const configuration: any = {};
      if (this.schemaSettings) {
        configuration.require_provenance = this.schemaSettings.requireProvenance;
        configuration.fast_conversion = this.schemaSettings.fastConversion;
        configuration.multiple_rows = this.schemaSettings.multiple_rows;
        configuration.schema_splitting_threshold = this.schemaSettings.schemaSplittingThreshold;
        configuration.n_shot = this.schemaSettings.n_shot;
      }
      if (this.schemaConfig?.configuration?.sheets) {
        configuration.sheets = this.schemaConfig.configuration.sheets;
      }
      if (Object.keys(configuration).length) {
        dto.configuration = configuration;
      }
      return JSON.stringify(dto, null, 2);
    }

    return JSON.stringify(schemaObject, null, 2);
  }
}
